import React, { useCallback, useEffect } from 'react'

type ModalVariant = 'info' | 'success' | 'warning' | 'error'

interface FeedbackModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  message: string
  variant?: ModalVariant
  actionLabel?: string
  onAction?: () => void
  secondaryLabel?: string
  onSecondary?: () => void
  children?: React.ReactNode
}

const variantConfig: Record<
  ModalVariant,
  { icon: React.ReactNode; bgColor: string; iconBg: string }
> = {
  info: {
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    bgColor: 'from-blue-500/20 to-blue-600/10',
    iconBg: 'from-blue-500/30 to-blue-600/20',
  },
  success: {
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    bgColor: 'from-emerald-500/20 to-emerald-600/10',
    iconBg: 'from-emerald-500/30 to-emerald-600/20',
  },
  warning: {
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
    bgColor: 'from-amber-500/20 to-amber-600/10',
    iconBg: 'from-amber-500/30 to-amber-600/20',
  },
  error: {
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    bgColor: 'from-red-500/20 to-red-600/10',
    iconBg: 'from-red-500/30 to-red-600/20',
  },
}

export default function FeedbackModal({
  isOpen,
  onClose,
  title,
  message,
  variant = 'info',
  actionLabel = 'OK',
  onAction,
  secondaryLabel,
  onSecondary,
  children,
}: FeedbackModalProps) {
  const config = variantConfig[variant]

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, handleEscape])

  const handleAction = () => {
    if (onAction) {
      onAction()
    }
    onClose()
  }

  const handleSecondary = () => {
    if (onSecondary) {
      onSecondary()
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-md bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl sm:rounded-3xl shadow-2xl border border-white/10 animate-scaleIn overflow-hidden`}
      >
        {/* Header gradient */}
        <div
          className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${config.bgColor}`}
        />

        <div className="p-6 sm:p-8">
          {/* Icon */}
          <div className="flex justify-center mb-5">
            <div
              className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${config.iconBg} flex items-center justify-center text-white shadow-lg`}
            >
              {config.icon}
            </div>
          </div>

          {/* Title */}
          <h2
            id="modal-title"
            className="text-xl sm:text-2xl font-bold text-white text-center mb-3"
          >
            {title}
          </h2>

          {/* Message */}
          <p className="text-slate-300 text-center text-sm sm:text-base leading-relaxed mb-6">
            {message}
          </p>

          {/* Custom content */}
          {children && <div className="mb-6">{children}</div>}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {secondaryLabel && (
              <button
                onClick={handleSecondary}
                className="px-6 py-3 rounded-xl text-slate-300 hover:text-white hover:bg-white/10 transition-all duration-200 font-medium text-sm sm:text-base"
              >
                {secondaryLabel}
              </button>
            )}
            <button
              onClick={handleAction}
              className="px-8 py-3 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white font-semibold shadow-lg shadow-red-500/30 hover:shadow-red-500/40 transition-all duration-200 text-sm sm:text-base min-w-[120px]"
            >
              {actionLabel}
            </button>
          </div>
        </div>
      </div>

      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out forwards;
        }

        .animate-scaleIn {
          animation: scaleIn 0.25s ease-out forwards;
        }
      `}</style>
    </div>
  )
}

// Hook for easier modal management
export function useFeedbackModal() {
  const [modalState, setModalState] = React.useState<{
    isOpen: boolean
    title: string
    message: string
    variant: ModalVariant
    actionLabel?: string
    onAction?: () => void
    secondaryLabel?: string
    onSecondary?: () => void
    children?: React.ReactNode
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info',
  })

  const showModal = (
    options: Omit<typeof modalState, 'isOpen'> & { isOpen?: boolean },
  ) => {
    setModalState({ ...options, isOpen: true })
  }

  const hideModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }))
  }

  return {
    modalState,
    showModal,
    hideModal,
  }
}
